import { Injectable, Injector, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  Auth,
  UserCredential,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from '@angular/fire/auth';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { concatMap, from, map, skip, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { Office } from './model';
import { OfficeService } from './office.service';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userCredential = signal<UserCredential | null>(null);
  isLoggedIn = signal<boolean>(false);

  private officeService: OfficeService = inject(OfficeService);
  private logger: LogService = inject(LogService);
  private auth: Auth = inject(Auth);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private injector: Injector = inject(Injector);

  private redirectUrl: string | null = null;

  constructor() {
    // const credentialFromSession: string | null = sessionStorage.getItem('UserCredential');
    const credentialFromSession: string | null = localStorage.getItem('UserCredential');
    if (credentialFromSession !== null) {
      const cred: UserCredential = <UserCredential>JSON.parse(credentialFromSession);
      this.userCredential.set(cred);
    }

    onAuthStateChanged(this.auth, user => {
      if (user) {
        console.log('########### Benutzer ist angemeldet:', user);
        // Benutzer ist eingeloggt und wird in allen Tabs synchronisiert
      } else {
        console.log('########### Benutzer ist nicht angemeldet');
        // Hier könnte eine Neuanmeldung initiiert werden, falls notwendig
      }
    });
  }

  isAuthenticated() {
    return this.userCredential() !== null;
  }

  isAuthenticatedOrRedirect(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (this.userCredential() !== null) {
      return true;
    } else {
      // this.redirectUrl = this.route.snapshot.url.join('/');
      this.redirectUrl = state.url;
      console.log('redirectURL ' + this.redirectUrl);

      this.router.navigate(['auth']);
      return false;
    }
  }

  signUp(email: string, password: string, officeName?: string, contactData?: string) {
    this.wipeSessionStorge();
    return createUserWithEmailAndPassword(this.auth, email, password).then(result => {
      this.setUserCredential(result);
      console.log('Sign up successful', result);
      const office: Office = {
        name: officeName ?? '',
        contactData: contactData ?? '',
        isCategoryDescriptionsEnabled: true,
      };
      this.officeService.createOffice(office, result.user.uid).then(result => {
        this.router.navigate(['account']);
        console.log('Office creation successful', result);
      });
    });
  }

  login(email: string, password: string) {
    this.wipeSessionStorge();
    const logout = from(signOut(this.auth));
    const signin = from(signInWithEmailAndPassword(this.auth, email, password));

    return logout.pipe(
      concatMap(signoutResult => {
        console.log('signoutResult ', signoutResult);
        return signin;
      }),
      map(result => {
        this.setUserCredential(result);
        console.log('before getoffice');

        this.officeService.getOffice(result.user.uid);
        toObservable(this.officeService.office, { injector: this.injector })
          .pipe(skip(1), take(1))
          .subscribe(() => {
            console.log('--------------------now routing to patients');
            console.log('redirectURL ' + this.redirectUrl);
            if (this.redirectUrl) {
              this.router.navigateByUrl(this.redirectUrl);
              this.redirectUrl = null;
            } else {
              this.router.navigate(['patients']);
            }
          });
        console.log('Login successful', result);
        this.logger.setUid(email);
        this.logger.log('login success ', { email });
        this.isLoggedIn.set(true);
      })
      // catchError(error => {
      //   console.log('catcherror' + JSON.stringify(error));
      //   return of(error.code);
      // })
    );
  }

  logout() {
    console.log('logout');
    this.logger.log('logout', {});
    this.wipeSessionStorge();
    const logout = from(signOut(this.auth));
    logout.subscribe(signoutResult => {
      console.log('signoutResult ', signoutResult);
      this.isLoggedIn.set(false);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });
  }

  updatePassword(password: string) {
    this.logger.log('updatePassword', {});
    return updatePassword(this.auth.currentUser!, password);
  }

  sendResetLink(email: string) {
    console.log('sendResetLink', email);
    this.logger.log('sendResetLink', { email });

    return sendPasswordResetEmail(this.auth, email, { url: environment.baseUrl });
  }

  private setUserCredential(userCredential: UserCredential): void {
    this.userCredential.set(userCredential);
    // sessionStorage.setItem('UserCredential', JSON.stringify(userCredential));
    localStorage.setItem('UserCredential', JSON.stringify(userCredential));
  }

  private wipeSessionStorge(): void {
    // sessionStorage.removeItem('UserCredential');
    // sessionStorage.removeItem('Office');
    localStorage.removeItem('UserCredential');
    localStorage.removeItem('Office');
    localStorage.removeItem('uuid');
    this.userCredential.set(null);
  }
}
